<script setup>
import Modal from "../../../../../components/ui-components/modal/Modal.vue";
import { ref, defineExpose } from "vue";
import { useRouter } from 'vue-router'
import Loading from 'vue3-loading-overlay';
import {usePageLoadingStore} from "../../../../../stores/pageLoading";
import _ from "lodash";

const router = useRouter();
const loadingStore = usePageLoadingStore()

const baseModal = ref(null);
const resolvePromise = ref(null);

const modalBody = ref(null)
const alertModal = ref(null)
const confirmModal = ref(null)
const modalContent = ref([])
const okButtonText = ref("네");
const cancelButtonText = ref("아니오");

const loaded = ref(true)

const userRef = ref({}); // 사용자
const resumeRef = ref({}); // 이력서 정보
const desiredJobRef = ref({}); // 희망 직무
const interestsRef = ref({}); // 관심 카테고리

const show = () => {
  baseModal.value.open();
  return new Promise((resolve, _) => {
    resolvePromise.value = resolve;
  });
};

const confirm = async () => {
  baseModal.value.close();
  resolvePromise.value(true);
};

const cancel = () => {
  baseModal.value.close();
  resolvePromise.value(false);
};

const init = (user, recordCreate, data_desiredJob, data_interests) => {
  userRef.value = user;
  resumeRef.value = recordCreate;

  if (data_desiredJob) {
    desiredJobRef.value = _.map(_.filter(data_desiredJob, {'on': true}), 'value').join(',');
  }
  if (data_interests) {
    interestsRef.value = _.map(_.filter(data_interests.value, {'on': true}), 'value').join(',');
  }
}

defineExpose({
  show,
  init,
})
</script>

<template>
  <Modal ref="baseModal" class="vld-parent">
    <loading
        :active="!loaded"
        loader="dots"
        color="#3843AF"
        opacity="0.5"
        width="35"
        :is-full-page="false"></loading>
    <div class="modal-inner xl-size">
      <button class="close" @click="cancel"></button>
      <div class="modal-body" v-show="loaded">
        <div class="resume-wrap">
          <div class="resume-title">
            {{ resumeRef.title }}
          </div>
          <div class="self-info">
            <h1>{{ userRef.userName }}</h1>
            <p><span>{{ $phoneNum(userRef.phoneNum) }}</span><span>{{ userRef.email }}</span></p>
          </div>
          <!-- s : 기본정보 -->
          <section class="basic">
            <dl v-if="desiredJobRef">
              <dt>희망 직무</dt>
              <dd>{{ desiredJobRef }}</dd>
            </dl>
            <dl v-if="interestsRef">
              <dt>관심 카테고리</dt>
              <dd>{{ interestsRef }}</dd>
            </dl>
            <dl v-if="resumeRef.desiredSalary">
              <dt>희망 연봉</dt>
              <dd>
                <span>{{ $number(resumeRef.desiredSalary) }}만원 이상</span>
              </dd>
            </dl>
            <div class="word-break-all" v-if="resumeRef.introOpenYn !== 'N' && (resumeRef.introAline || resumeRef.selfIntro)">
              <h2>{{ resumeRef.introAline }}</h2>
              <p class="text" v-html="resumeRef.selfIntro.replaceAll('\n', '</br>')"></p>
            </div>
          </section>
          <!-- e : 기본정보 -->

          <!-- s : 학력 -->
          <section v-if="resumeRef.eduHistOpenYn !== 'N' && resumeRef.eduHistoris && resumeRef.eduHistoris.length > 0">
            <h2>학력</h2>
            <dl v-for="eduHist in resumeRef.eduHistoris">
              <dt>
                <b>{{ $yyyymm(eduHist.startYm) }} ~ {{ eduHist.eduTenureGb !== '03' ? $yyyymm(eduHist.endYm) : ''}}</b>
                {{ $gbNameKR('SCHOOL_GB',eduHist.schoolGb) }} {{ $gbNameKR('EDU_TENURE_GB',eduHist.eduTenureGb) }}
              </dt>
              <dd>
                <b>{{ eduHist.schoolName }}</b>
                <p v-if="eduHist.schoolGb !== '05'">주전공 {{ $gbNameKR('MAJOR_GB', eduHist.majorGb) }} {{ eduHist.majorName }}</p>
                <p v-if="eduHist.submajorName">{{ $gbNameKR('SUBMAJOR_TYPE_GB', eduHist.submajorTypeGb) }} {{ $gbNameKR('MAJOR_GB', eduHist.submajorGb) }} {{ eduHist.submajorName }}</p>
                <p v-if="eduHist.schoolGb !== '05'">{{ eduHist.score }} / {{ $gbNameKR('FULL_SCORE_GB', eduHist.fullScoreGb) }} 만점</p>
                <p class="file" v-if="eduHist.transcriptName" @click="$downloadForPrivate({fileName:eduHist.transcriptName, fileDir: eduHist.transcriptDir })">{{ eduHist.transcriptName }}
                  <span v-if="eduHist.transcriptSize">({{ $fileSize(eduHist.transcriptSize) }})</span>
                </p>
              </dd>
            </dl>
          </section>
          <!-- e : 학력 -->

          <!-- s : 포트폴리오 -->
          <section v-if="resumeRef.portfolioOpenYn !== 'N' && resumeRef.portfolios && resumeRef.portfolios.length > 0">
            <h2>포트폴리오</h2>
            <dl v-for="pofol in resumeRef.portfolios">
              <dt>
                <b>{{ $yyyymm(pofol.startYm) }} {{ pofol.pofolGb !== '01' && pofol.pofolGb !== '04' ? '~' : '' }} {{ pofol.pofolGb !== '01' && pofol.pofolGb !== '04' ? $yyyymm(pofol.endYm) : '' }}</b>
              </dt>
              <dd class="word-break-all">
                <b>{{ $gbNameKR('POFOL_GB', pofol.pofolGb) }}</b>
                <p>{{ pofol.pofolName }}</p>
                <p>{{ pofol.pofolOrgan }}</p>
                <p class="text" v-if="pofol.pofolGb !== '01'" v-html="pofol.pofolContent.replaceAll('\n', '<br/>')"></p>
                <p class="file" v-for="portfolioAttach in pofol.portfolioAttachs" @click="$downloadForPrivate(portfolioAttach)">
                  {{ portfolioAttach.fileName }}<span v-if="portfolioAttach.fileSize">({{ $fileSize(portfolioAttach.fileSize)}})</span>
                </p>
              </dd>
            </dl>
          </section>
          <!-- e : 포트폴리오 -->

          <!-- s : 자격증 및 외국어 -->
          <section v-if="resumeRef.certForeignOpenYn !== 'N' && ((resumeRef.certificates && resumeRef.certificates.length > 0) || (resumeRef.languageTests && resumeRef.languageTests.length > 0) || (resumeRef.foreignLanguages && resumeRef.foreignLanguages.length > 0))">
            <h2>자격증 및 외국어</h2>
            <div v-if="resumeRef.certificates && resumeRef.certificates.length > 0">
              <h3>자격증</h3>
              <dl v-for="cert in resumeRef.certificates">
                <dt>
                  {{ cert.getYy }}
                </dt>
                <dd>
                  <b>{{ cert.certName }}</b>
                  <p>{{ cert.certOrgan }}</p>
                </dd>
              </dl>
            </div>
            <div v-if="resumeRef.languageTests && resumeRef.languageTests.length > 0">
              <h3>어학 시험</h3>
              <dl v-for="languageTest in resumeRef.languageTests">
                <dt>
                  {{ $yyyymm(languageTest.getYmd) }}
                </dt>
                <dd>
                  <b>{{ $gbNameKR('LANG_GB', languageTest.langGb) }}</b>
                  <p>시험 종류: {{ languageTest.testType }} / 점수: {{ languageTest.testScore }}</p>
                </dd>
              </dl>
            </div>
            <div v-if="resumeRef.foreignLanguages && resumeRef.foreignLanguages.length > 0">
              <h3>외국어</h3>
              <dl v-for="foreignLanguage in resumeRef.foreignLanguages">
                <dt>
                  <b>{{ foreignLanguage.foreignLangName }}</b>
                </dt>
                <dd>
                  <p>{{ $gbNameKR('LANG_LEVEL_GB', foreignLanguage.langLevelGb) }}</p>
                </dd>
              </dl>
            </div>
          </section>
          <!-- e : 자격증 및 외국어 -->

          <!-- s : 업무 경험 -->
          <section v-if="resumeRef.jobHistOpenYn !== 'N' && resumeRef.jobHistorys && resumeRef.jobHistorys.length > 0">
            <h2>업무 경험</h2>
            <dl v-for="jobHist in resumeRef.jobHistorys">
              <dt>
                <b>{{ jobHist.companyName }}</b>
                {{ $yyyymm(jobHist.startYm) }} ~ {{ jobHist.tenureYn === 'Y' ? '재직중' : $yyyymm(jobHist.endYm) }}
              </dt>
              <dd class="word-break-all">
                <p>{{ jobHist.jobTitle }} <span>{{ jobHist.departName }}</span></p>
                <p class="text" v-html="jobHist.jobNote.replaceAll('\n', '</br>')"></p>
              </dd>
            </dl>
          </section>
          <!-- e : 업무 경험 -->

          <!-- s : 병역 -->
          <section class="army" v-if="resumeRef.milOpenYn !== 'N' && resumeRef.milServiceGb">
            <dl>
              <dt><h2>병역</h2></dt>
              <dd><p>{{ $gbNameKR('MIL_SERVICE_GB', resumeRef.milServiceGb) }}</p></dd>
            </dl>
          </section>
          <!-- e : 병역 -->
        </div>
      </div>
    </div>
  </Modal>

</template>

<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";
.modal-inner {
  margin-top: 50px;
  .close {
    top: -20px !important;
  }

  .modal-body {
    .resume-wrap {
      background-color: $gray-thin_color;
      padding: 30px;
      .resume-title {
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        border-bottom: 2px solid $black_color;
        @include font-size_xxl;
        font-weight: bold;
        text-align: center;
        padding: 0 20px 20px;
        margin-bottom: 20px;
        //background: #ffffff;
      }
      .self-info {
        border-bottom: 2px solid $black_color;
        h1 {
          @include font-size_xl;
          margin-bottom: 10px;
        }
        p {
          @include font-size_md;
          margin-bottom: 20px;
          span {
            position: relative;
            margin-left: 20px;
            padding-left: 20px;
            &:before {
              content: "";
              position: absolute;
              top: 50%;
              left: 0;
              width: 1px;
              height: 14px;
              background: $gray-regular_color;
              -webkit-transform: translateY(-50%);
              transform: translateY(-50%);
            }
            &:first-child {
              padding: 0;
              margin: 0;
              &:before {
                display: none;
              }
            }
          }
        }
      }
      section {
        border-bottom: 1px solid $black_color;
        padding: 30px 0;
        &:last-child {
          border-bottom: 0;
          padding: 30px 0;
        }
        &.basic {
          div {
            border-top: $border_line;
            padding-top: 30px;
            h2 {
              margin-bottom: 10px;
            }
            p {
              word-break: break-word;
            }
          }
        }
        &.army {
          h2 {
            color: $black_color;
          }
        }
        h2 {
          @include font-size_md;
          margin-bottom: 20px;
        }
        dl {
          display: flex;
          margin-bottom: 30px;
          &:last-child {
            margin-bottom: 0;
          }
          dt {
            width: 192px;
            color: $gray_color;
            b {
              display: block;
              color: $black_color;
              margin-bottom: 6px;
            }
          }
          dd {
            width: calc(100% - 192px);
            b {
              display: block;
              margin-bottom: 6px;
            }
            p {
              span {
                position: relative;
                padding-left: 10px;
                margin-left: 10px;
                &:before {
                  content: "";
                  position: absolute;
                  top: 50%;
                  left: 0;
                  width: 1px;
                  height: 10px;
                  background: $gray-regular_color;
                  -webkit-transform: translateY(-50%);
                  transform: translateY(-50%);
                }
              }
            }
            .file {
              color: $blue_color;
              cursor: pointer;
            }
          }
        }
        div {
          padding-bottom: 20px;
          border-bottom: $border_line;
          &:last-child {
            border-bottom: 0;
            padding-bottom: 0;
          }
          h3 {
            @include font-size_md;
            color: $gray_color;
            margin-top: 20px;
            margin-bottom: 6px;
          }
        }
      }
    }
  }
  .modal-footer {
    padding: 0 30px;
  }
}
</style>